import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

interface DeleteDialogProps<T> {
  /** The record to delete; the dialog is open while this is non-null. */
  item: T | null
  onClose: () => void
  title: (item: T) => string
  description?: (item: T) => ReactNode
  onDelete: (item: T) => Promise<unknown>
  successMessage: (item: T) => string
}

/**
 * Confirmation + delete call. Conflicts (409, e.g. "still in use") and other failures are shown
 * as an error toast and the dialog closes, since retrying would fail the same way.
 */
export function DeleteDialog<T>({
  item,
  onClose,
  title,
  description,
  onDelete,
  successMessage,
}: DeleteDialogProps<T>) {
  const { t } = useTranslation('academic')
  const [pending, setPending] = useState(false)

  const confirm = async () => {
    if (!item) return
    setPending(true)
    try {
      await onDelete(item)
      toast.success(successMessage(item))
    } catch (e) {
      toast.error(t('Could not delete'), errorMessage(e))
    } finally {
      setPending(false)
      onClose()
    }
  }

  return (
    <ConfirmDialog
      open={item !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={item ? title(item) : t('Delete')}
      description={item && description ? description(item) : t('This cannot be undone.')}
      confirmLabel={t('Delete')}
      loading={pending}
      onConfirm={() => void confirm()}
    />
  )
}
